import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { useArticlesByCollection, useReorderCollectionArticles } from "../../hooks/useArticles";
import { getErrorMessage } from "../../lib/apiErrors";
import { showToast } from "../../lib/toast";
import { ArticleStatus, ArticleStatusLabels, type ArticleDto, type ArticleStatus as ArticleStatusValue } from "../../types/articles";

interface ArrangeCollectionArticlesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  collectionId: string;
  collectionName: string;
}

/**
 * The reading order of the articles filed directly in a collection or section (a section's articles are arranged
 * from the section). Drafts are included so they're in place when published; archived articles go last.
 */
export function ArrangeCollectionArticlesDialog({
  open,
  onOpenChange,
  organizationId,
  collectionId,
  collectionName,
}: ArrangeCollectionArticlesDialogProps) {
  const { data, isLoading, error } = useArticlesByCollection(open ? collectionId : undefined, false, false);
  const reorder = useReorderCollectionArticles();
  const [order, setOrder] = useState<ArticleDto[]>([]);
  const [changed, setChanged] = useState(false);

  // Start from the saved order whenever it (re)loads, unless the arrangement here has been changed
  useEffect(() => {
    if (data && !changed) setOrder(data);
  }, [data, changed]);

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= order.length) return;
    const next = [...order];
    [next[index], next[target]] = [next[target], next[index]];
    setOrder(next);
    setChanged(true);
  };

  const handleSave = async () => {
    try {
      await reorder.mutateAsync({ collectionId, orderedIds: order.map((a) => a.id), organizationId });
      showToast.success("Order Saved", `Articles in "${collectionName}" now read in this order.`);
      setChanged(false);
      onOpenChange(false);
    } catch {
      // The hook shows why; a stale list is refetched, so start again from it
      setChanged(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Arrange articles</DialogTitle>
          <DialogDescription>
            The order readers see in "{collectionName}". Only articles filed directly here are listed - arrange a
            section's articles from the section.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-1 px-1">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-label="Loading articles" />
            </div>
          ) : error ? (
            <p className="text-sm text-destructive">{getErrorMessage(error, "Couldn't load the articles")}</p>
          ) : order.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No articles are filed directly in this collection.</p>
          ) : (
            <ol className="divide-y rounded-md border">
              {order.map((article, index) => (
                <li key={article.id} className="flex items-center gap-2 px-2 py-1.5">
                  <span className="w-6 text-xs text-muted-foreground text-right">{index + 1}</span>
                  <span className="flex-1 min-w-0 text-sm truncate" title={article.title}>
                    {article.title}
                  </span>
                  {article.status !== ArticleStatus.Published && (
                    <Badge variant="outline" className="text-[10px]">
                      {ArticleStatusLabels[article.status as ArticleStatusValue]}
                    </Badge>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => move(index, -1)}
                    disabled={index === 0 || reorder.isPending}
                    aria-label={`Move ${article.title} up`}
                  >
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => move(index, 1)}
                    disabled={index === order.length - 1 || reorder.isPending}
                    aria-label={`Move ${article.title} down`}
                  >
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ol>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!changed || reorder.isPending}>
            {reorder.isPending ? "Saving..." : "Save Order"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
